import React, { useEffect, useState } from 'react'
import i18n from 'i18next'
import { useTranslation } from 'react-i18next'
import { chineseLang } from '../../i18n/chinese'
import './writingSpeaking.css'

/*
 * Teacher dashboard pieces for the Writing & Speaking step: the speaking mode
 * chip, the "Words to use" strip and the written-word underlines. Correction
 * docs created before the step have none of these fields, and every piece
 * here renders nothing (or the plain answer) for them.
 */

export type SpeakingMode = 'read_writing' | 'free_talk' | 'read_passage'

export interface WordHeard {
  word: string
  atSeconds: number
}

const SPEAKING_MODES: SpeakingMode[] = ['read_writing', 'free_talk', 'read_passage']

/** Legacy taster docs stored 'read_sentence', which is today's Read a passage. */
export const normalizeSpeakingMode = (mode: unknown): SpeakingMode | null => {
  if (mode === 'read_sentence') return 'read_passage'
  return SPEAKING_MODES.indexOf(mode as SpeakingMode) !== -1 ? (mode as SpeakingMode) : null
}

// Translation keys.
const SHORT_LABELS: Record<SpeakingMode, string> = {
  read_writing: 'writeSpeak:dashboard.modeName.read_writing',
  free_talk: 'writeSpeak:dashboard.modeName.free_talk',
  read_passage: 'writeSpeak:dashboard.modeName.read_passage',
}

export const speakingModeShortLabel = (mode: SpeakingMode): string => i18n.t(SHORT_LABELS[mode])

/** passageIndex is 0-based (the learner sees "Paragraph 1 of N" for index 0). */
export const speakingModeLabel = (mode: SpeakingMode, passageIndex?: unknown): string => {
  if (mode === 'read_passage' && typeof passageIndex === 'number' && passageIndex >= 0) {
    return i18n.t('writeSpeak:dashboard.passageWithIndex', { index: Math.floor(passageIndex) + 1 })
  }
  return i18n.t(SHORT_LABELS[mode])
}

/** The 0-based paragraph the learner read aloud, or null when they did not read a passage. */
export const readPassageIndex = (data: any): number | null => {
  if (normalizeSpeakingMode(data?.speakingMode) !== 'read_passage') return null
  const index = data?.passageIndex
  return typeof index === 'number' && Number.isFinite(index) && index >= 0 ? Math.floor(index) : null
}

export const formatSeconds = (seconds: number): string => {
  const total = Math.max(0, Math.floor(seconds))
  return `${Math.floor(total / 60)}:${`0${total % 60}`.slice(-2)}`
}

const stringList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((w): w is string => typeof w === 'string' && w.trim() !== '') : []

const heardList = (value: unknown): WordHeard[] =>
  Array.isArray(value)
    ? value.filter(
        (h): h is WordHeard =>
          !!h && typeof h.word === 'string' && typeof h.atSeconds === 'number' && Number.isFinite(h.atSeconds),
      )
    : []

/** True when the doc carries any Writing & Speaking field worth reading audio for. */
export const hasWritingSpeakingData = (data: any): boolean =>
  !!normalizeSpeakingMode(data?.speakingMode) || stringList(data?.practiceWords).length > 0

/**
 * Taster submissions used to store a stand-in sentence such as "(Voice-only
 * submission: reading a passage from the story.)" as the answer when the learner
 * only recorded. That text is not the learner's writing: there is nothing to
 * correct, and it must never be diffed against the teacher's text.
 */
const VOICE_ONLY_PLACEHOLDER = /^\(Voice-only submission:/

/** The learner's written answer, or '' when they only recorded. */
export const writtenAnswer = (data: any): string => {
  const answer = typeof data?.answer === 'string' ? data.answer.trim() : ''
  return VOICE_ONLY_PLACEHOLDER.test(answer) ? '' : answer
}

/** True when the learner sent a recording the teacher should answer with one. */
export const hasLearnerRecording = (data: any): boolean =>
  typeof data?.recordingUrl === 'string' && data.recordingUrl.trim() !== ''

/** Duration of the learner's recording once metadata loads; null while unknown. */
const useAudioDuration = (audio: HTMLAudioElement | null): number | null => {
  const [duration, setDuration] = useState<number | null>(null)

  useEffect(() => {
    if (!audio) {
      setDuration(null)
      return undefined
    }
    const read = () => setDuration(Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : null)
    read()
    audio.addEventListener('loadedmetadata', read)
    audio.addEventListener('durationchange', read)
    return () => {
      audio.removeEventListener('loadedmetadata', read)
      audio.removeEventListener('durationchange', read)
    }
  }, [audio])

  return duration
}

interface ModeChipProps {
  data: any
  audio: HTMLAudioElement | null
}

export const SpeakingModeChip: React.FC<ModeChipProps> = ({ data, audio }) => {
  const duration = useAudioDuration(audio)
  const mode = normalizeSpeakingMode(data?.speakingMode)
  if (!mode) return null
  const label = speakingModeLabel(mode, data?.passageIndex)

  return (
    <span className='dash-ws-mode'>
      {duration !== null ? i18n.t('writeSpeak:dashboard.chip', { label, time: formatSeconds(duration) }) : label}
    </span>
  )
}

interface WordsStripProps {
  data: any
  audio: HTMLAudioElement | null
}

export const WordsToTryStrip: React.FC<WordsStripProps> = ({ data, audio }) => {
  const { t } = useTranslation('writeSpeak')
  // The teacher's view has no learner character setting: the words show as the learner's step stored them.
  const zhLang = chineseLang()
  const practiceWords = stringList(data?.practiceWords)
  if (!practiceWords.length) return null

  const written = stringList(data?.wordsWritten)
  const heard = heardList(data?.wordsHeard)

  const playFrom = (atSeconds: number) => {
    if (!audio) return
    audio.currentTime = Math.max(0, atSeconds)
    const played = audio.play()
    if (played && typeof played.catch === 'function') played.catch(() => undefined)
  }

  return (
    <div className='dash-ws-words'>
      <span className='dash-ws-words-label'>{t('dashboard.wordsLabel')}</span>
      {practiceWords.map((word, index) => {
        const isWritten = written.indexOf(word) !== -1
        const firstHeard = heard
          .filter((h) => h.word === word)
          .reduce<WordHeard | null>((first, h) => (!first || h.atSeconds < first.atSeconds ? h : first), null)
        const saidAt = firstHeard ? formatSeconds(firstHeard.atSeconds) : ''

        if (firstHeard && audio) {
          return (
            <button
              key={`${word}-${index}`}
              type='button'
              className={`dash-ws-word ${isWritten ? 'is-written' : 'is-said'}`}
              onClick={() => playFrom(firstHeard.atSeconds)}
              aria-label={t('dashboard.playFrom', { word, time: saidAt })}
            >
              <span lang={zhLang}>{word}</span>
              <em>
                {isWritten ? t('dashboard.writtenPrefix') : ''}
                <span className='dash-ws-said'>{t('dashboard.said', { time: saidAt })}</span>
              </em>
            </button>
          )
        }

        const label = [isWritten ? t('dashboard.written') : '', firstHeard ? t('dashboard.said', { time: saidAt }) : '']
          .filter(Boolean)
          .join(' · ')
        return (
          <span
            key={`${word}-${index}`}
            className={`dash-ws-word ${isWritten ? 'is-written' : firstHeard ? 'is-said-static' : 'is-unused'}`}
          >
            <span lang={zhLang}>{word}</span>
            <em>{label || t('dashboard.notUsed')}</em>
          </span>
        )
      })}
    </div>
  )
}

/** Splits `text` into runs, flagging every occurrence of a target word (longest words win overlaps). */
export const splitByWords = (text: string, words: string[]): Array<{ text: string; hit: boolean }> => {
  const targets = words.filter((w, i) => w && words.indexOf(w) === i).sort((a, b) => b.length - a.length)
  const owner: number[] = new Array(text.length).fill(-1)
  let rangeId = 0

  targets.forEach((word) => {
    let at = text.indexOf(word)
    while (at !== -1) {
      let free = true
      for (let i = at; i < at + word.length; i++) if (owner[i] !== -1) free = false
      if (free) {
        for (let i = at; i < at + word.length; i++) owner[i] = rangeId
        rangeId++
      }
      at = text.indexOf(word, at + word.length)
    }
  })

  const runs: Array<{ text: string; hit: boolean; id: number }> = []
  for (let i = 0; i < text.length; i++) {
    const last = runs[runs.length - 1]
    if (last && last.id === owner[i] && (owner[i] !== -1 || !last.hit)) last.text += text[i]
    else runs.push({ text: text[i], hit: owner[i] !== -1, id: owner[i] })
  }
  return runs.map(({ text: t, hit }) => ({ text: t, hit }))
}

interface AnswerProps {
  answer: string
  wordsWritten?: unknown
}

/** The learner's answer with each written target word underlined in green. */
export const AnswerWithWrittenWords: React.FC<AnswerProps> = ({ answer, wordsWritten }) => {
  const written = stringList(wordsWritten)
  if (!written.length) return <>{answer}</>

  return (
    <>
      {splitByWords(answer, written).map((run, index) =>
        run.hit ? (
          <u key={index} className='dash-ws-written'>
            {run.text}
          </u>
        ) : (
          <React.Fragment key={index}>{run.text}</React.Fragment>
        ),
      )}
    </>
  )
}
